import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import styles from "./Modal.module.css";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Bold Title 3 heading at the top of the card. */
  title: string;
  /** Body-sized description below the title. Pass JSX if you need
   *  inline emphasis or links. */
  description?: ReactNode;
  /** Modal content — typically the form fields, a divider, then the
   *  primary action button. The Modal owns the surrounding chrome and
   *  dismissibility, but the body is fully consumer-controlled. */
  children: ReactNode;
  /** Accessible label override — defaults to the visible `title`. */
  "aria-label"?: string;
}

/**
 * Generic form modal — Figma node 272:10605. Centered card on a dim
 * backdrop with a header (title + close button), an optional
 * description, and a children slot for everything below. Dismisses on
 * Escape, backdrop click, and the close button.
 *
 * Below the 500 px breakpoint the modal collapses to a full-screen
 * sheet with no rounded corners — mirrors the "small viewport" Figma
 * spec where the modal essentially becomes the page.
 *
 * Locks body scroll while open so the page underneath doesn't shift
 * when the user interacts with the modal.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  "aria-label": ariaLabel,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Close on Escape; autofocus the first form field so keyboard users
  // land where they can start typing immediately. Buttons (including
  // the close X) are skipped intentionally.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    const firstField = cardRef.current?.querySelector<HTMLElement>(
      "input, textarea, select",
    );
    firstField?.focus();
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock page scroll while the modal is open so the background doesn't
  // scroll under a backdrop tap.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={cardRef} className={styles.card}>
        <header className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <IconButton
            variant="outline"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" />
          </IconButton>
        </header>
        {description && <p className={styles.description}>{description}</p>}
        {children}
      </div>
    </div>
  );
}
